import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search, Download, Filter, FileText, Loader2,
  RefreshCw, ChevronLeft, ChevronRight, ShoppingCart, TrendingUp,
  ClipboardCheck, ChevronDown, Eye, Calendar
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatarMoeda, formatarData } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import { useEmpresa } from '../contexts/EmpresaContext';
import clsx from 'clsx';
import DocumentoVisualizadorModal from '../components/documentos/DocumentoVisualizadorModal';
import ModalFechamento from '../components/documentos/ModalFechamento';

const TIPOS     = [{ v: '', l: 'Todos os tipos' }, { v: 'nfe', l: 'NF-e' }, { v: 'nfce', l: 'NF-Ce' }, { v: 'nfse', l: 'NF-Se' }, { v: 'cte', l: 'CT-e' }, { v: 'mdfe', l: 'MDF-e' }, { v: 'ciot', l: 'CIOT' }];
const SITUACOES = [{ v: '', l: 'Todas' }, { v: 'autorizado', l: 'Autorizado' }, { v: 'cancelado', l: 'Cancelado' }, { v: 'pendente', l: 'Pendente' }, { v: 'denegado', l: 'Denegado' }];
const OPERACOES = [{ v: '', l: 'Compras e Vendas' }, { v: 'recebimento', l: 'Compras (recebimento)' }, { v: 'emissao', l: 'Vendas (emissão)' }];

function BadgeSituacao({ situacao }) {
  const cls = {
    autorizado: 'badge-autorizado',
    cancelado:  'badge-cancelado',
    pendente:   'badge-pendente',
    denegado:   'badge-denegado',
  };
  return <span className={cls[situacao] || 'badge-pendente'}>{situacao}</span>;
}

const MANIFESTA_OPCOES = [
  { tipo: 'ciencia',       label: 'Ciência da Operação',      cor: 'text-sky-400',    bg: 'bg-sky-900/40' },
  { tipo: 'confirmacao',   label: 'Confirmação da Operação',  cor: 'text-emerald-400', bg: 'bg-emerald-900/40' },
  { tipo: 'desconhecido',  label: 'Desconhecimento',          cor: 'text-red-400',    bg: 'bg-red-900/40' },
  { tipo: 'nao_realizado', label: 'Operação Não Realizada',   cor: 'text-amber-400',  bg: 'bg-amber-900/40' },
];

const MANIFESTA_BADGE = {
  ciencia:       { label: 'Ciência',       cls: 'text-sky-400 bg-sky-900/40' },
  confirmado:    { label: 'Confirmado',    cls: 'text-emerald-400 bg-emerald-900/40' },
  desconhecido:  { label: 'Desconhecido', cls: 'text-red-400 bg-red-900/40' },
  nao_realizado: { label: 'Não Realizado', cls: 'text-amber-400 bg-amber-900/40' },
};

function BotaoManifesta({ docId, manifestacaoAtual, onAtualizar }) {
  const [aberto,    setAberto]    = useState(false);
  const [enviando,  setEnviando]  = useState(null);
  const ref = useRef();

  useEffect(() => {
    function fechar(e) { if (ref.current && !ref.current.contains(e.target)) setAberto(false); }
    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, []);

  async function manifestar(tipo) {
    setEnviando(tipo);
    setAberto(false);
    try {
      const { data } = await api.post(`/manifesta/${docId}`, { tipo });
      toast.success(data.mensagem || 'Manifestação registrada!');
      onAtualizar(docId, tipo);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao manifestar.');
    } finally {
      setEnviando(null);
    }
  }

  const badge = manifestacaoAtual ? MANIFESTA_BADGE[manifestacaoAtual] : null;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setAberto(v => !v)}
        disabled={!!enviando}
        className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${
          badge
            ? `${badge.cls} hover:opacity-80`
            : 'text-slate-500 bg-slate-800/50 hover:text-primary-400 hover:bg-primary-900/20'
        }`}>
        {enviando
          ? <Loader2 size={11} className="animate-spin" />
          : <ClipboardCheck size={11} />
        }
        {badge ? badge.label : 'Manifestar'}
        <ChevronDown size={9} />
      </button>

      {aberto && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-[#0d1424] border border-surface-border rounded-lg shadow-2xl min-w-[200px] py-1">
          {MANIFESTA_OPCOES.map(op => (
            <button
              key={op.tipo}
              onClick={() => manifestar(op.tipo)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-surface-card transition-colors ${op.cor}`}>
              {op.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BadgeOperacao({ operacao }) {
  if (!operacao) return null;
  return operacao === 'recebimento'
    ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-900/40 text-sky-400 font-medium">COMPRA</span>
    : <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-400 font-medium">VENDA</span>;
}

export default function DocumentosPage() {
  const { usuario }      = useAuth();
  const { empresaAtual, carregando: carregandoEmpresa } = useEmpresa() || {};
  const [searchParams]   = useSearchParams();
  const operacaoUrl      = searchParams.get('operacao') || '';
  const isCliente        = usuario?.perfil === 'cliente';

  const [docs,              setDocs]              = useState([]);
  const [total,             setTotal]             = useState(0);
  const [paginas,           setPaginas]           = useState(1);
  const [pagina,            setPagina]            = useState(1);
  const [loading,           setLoading]           = useState(false);
  const [selecionados,      setSelecionados]      = useState([]);
  const [docVisualizado,    setDocVisualizado]    = useState(null);
  const [mostrarFechamento, setMostrarFechamento] = useState(false);

  // Mapeia documentoId → manifestacao local (atualiza sem re-buscar)
  const [manifestacoes, setManifestacoes] = useState({});

  function atualizarManifestacao(docId, tipo) {
    const mapa = { ciencia: 'ciencia', confirmacao: 'confirmado', desconhecido: 'desconhecido', nao_realizado: 'nao_realizado' };
    setManifestacoes(prev => ({ ...prev, [docId]: mapa[tipo] || tipo }));
  }

  const [filtros, setFiltros] = useState({
    tipo: '', situacao: '', data_inicio: '', data_fim: '', chave: '', operacao: operacaoUrl
  });

  // Sincroniza operacao da URL com os filtros (troca de "Compras" ↔ "Vendas" na sidebar)
  useEffect(() => {
    setFiltros(f => ({ ...f, operacao: operacaoUrl }));
    setPagina(1);
  }, [operacaoUrl]);

  const buscar = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = {
        pagina: pg, limite: 50, ...filtros,
        ...(empresaAtual && { empresa_id: empresaAtual.id }),
      };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await api.get('/documentos', { params });
      setDocs(data.documentos);
      setTotal(data.total);
      setPaginas(data.paginas);
      setPagina(pg);
      setSelecionados([]);
    } catch {
      toast.error('Erro ao buscar documentos.');
    } finally {
      setLoading(false);
    }
  }, [filtros, empresaAtual]);

  // Ticket 907: reconsulta ao trocar a empresa selecionada no seletor da sidebar
  useEffect(() => { if (!carregandoEmpresa) buscar(1); }, [filtros.operacao, empresaAtual, carregandoEmpresa]);

  function toggleSelecionado(id) {
    setSelecionados(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }

  function toggleTodos() {
    setSelecionados(prev => prev.length === docs.length ? [] : docs.map(d => d.id));
  }

  async function downloadXml(id) {
    try {
      const { data } = await api.get(`/documentos/${id}/download-xml`);
      window.open(data.url, '_blank');
    } catch {
      toast.error('Erro ao gerar link de download.');
    }
  }

  async function downloadLote() {
    if (!selecionados.length) return toast.error('Selecione pelo menos um documento.');
    const t = toast.loading(`Gerando ZIP com ${selecionados.length} documento(s)...`);
    try {
      const resp = await api.post('/documentos/download-lote', { ids: selecionados }, { responseType: 'blob' });
      const url  = URL.createObjectURL(resp.data);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `kairos_xmls_${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Download iniciado!', { id: t });
    } catch {
      toast.error('Erro ao gerar ZIP.', { id: t });
    }
  }

  // Título dinâmico baseado na operacao
  const titulo = operacaoUrl === 'recebimento'
    ? { label: 'Minhas Compras', icon: ShoppingCart, cor: 'text-sky-400' }
    : operacaoUrl === 'emissao'
    ? { label: 'Minhas Vendas',  icon: TrendingUp,   cor: 'text-emerald-400' }
    : { label: 'Documentos',     icon: FileText,      cor: 'text-primary-400' };

  const TituloIcon = titulo.icon;

  return (
    <div className="space-y-4">
      {/* Cabeçalho da página */}
      <div className="flex items-center gap-2">
        <TituloIcon size={18} className={titulo.cor} />
        <h1 className="text-base font-semibold text-slate-200">{titulo.label}</h1>
        {total > 0 && (
          <span className="text-xs text-slate-500 ml-1">
            — {total.toLocaleString('pt-BR')} documento(s)
          </span>
        )}
        <button
          onClick={() => setMostrarFechamento(true)}
          className="ml-auto btn-secondary text-xs py-1 px-2.5">
          <Calendar size={13} /> Fechamento
        </button>
      </div>

      {/* Filtros */}
      <div className="card space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <Filter size={14} className="text-slate-500" />
          <span className="text-xs font-medium text-slate-500">Filtros</span>
        </div>
        <div className={clsx('grid gap-3', isCliente ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-2 lg:grid-cols-5')}>
          {/* Operação — só admin/contador vê o dropdown aqui (cliente controla pela sidebar) */}
          {!isCliente && (
            <select className="input text-sm" value={filtros.operacao}
              onChange={e => setFiltros(f => ({ ...f, operacao: e.target.value }))}>
              {OPERACOES.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          )}
          <select className="input text-sm" value={filtros.tipo}
            onChange={e => setFiltros(f => ({ ...f, tipo: e.target.value }))}>
            {TIPOS.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <select className="input text-sm" value={filtros.situacao}
            onChange={e => setFiltros(f => ({ ...f, situacao: e.target.value }))}>
            {SITUACOES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <input type="date" className="input text-sm" value={filtros.data_inicio}
            onChange={e => setFiltros(f => ({ ...f, data_inicio: e.target.value }))} />
          <input type="date" className="input text-sm" value={filtros.data_fim}
            onChange={e => setFiltros(f => ({ ...f, data_fim: e.target.value }))} />
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input className="input text-sm pl-8" placeholder="Chave de acesso..."
              value={filtros.chave} onChange={e => setFiltros(f => ({ ...f, chave: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && buscar(1)} />
          </div>
          <button onClick={() => buscar(1)} className="btn-primary text-sm py-1.5 px-3">
            <Search size={14} /> Buscar
          </button>
          <button onClick={() => {
            setFiltros({ tipo: '', situacao: '', data_inicio: '', data_fim: '', chave: '', operacao: operacaoUrl });
          }} className="btn-secondary text-sm py-1.5 px-3">
            <RefreshCw size={14} /> Limpar
          </button>
          {selecionados.length > 0 && (
            <button onClick={downloadLote} className="btn-primary text-sm py-1.5 px-3 ml-auto">
              <Download size={14} /> Baixar ZIP ({selecionados.length})
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border bg-surface/40">
                <th className="px-4 py-3 text-left">
                  <input type="checkbox"
                    checked={selecionados.length === docs.length && docs.length > 0}
                    onChange={toggleTodos} className="rounded" />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Número</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                  {operacaoUrl === 'recebimento' ? 'Fornecedor' : operacaoUrl === 'emissao' ? 'Cliente' : 'Emitente'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">
                  {operacaoUrl === 'recebimento' ? 'Destinatário' : 'Destinatário'}
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Valor</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Emissão</th>
                {!operacaoUrl && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Op.</th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Situação</th>
                {operacaoUrl === 'recebimento' && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Manifest.</th>
                )}
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {loading ? (
                <tr><td colSpan={11} className="text-center py-12">
                  <Loader2 size={24} className="animate-spin text-primary-500 mx-auto" />
                </td></tr>
              ) : docs.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-12 text-slate-600">
                  <TituloIcon size={32} className="mx-auto mb-2 opacity-30" />
                  <p>Nenhum documento encontrado</p>
                  {isCliente && operacaoUrl === 'recebimento' && (
                    <p className="text-xs mt-1 text-slate-700">As notas fiscais de compra aparecem aqui após sincronização</p>
                  )}
                </td></tr>
              ) : docs.map(doc => (
                <tr key={doc.id}
                  className={clsx('hover:bg-surface-card/50 transition-colors', selecionados.includes(doc.id) && 'bg-primary-600/5')}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selecionados.includes(doc.id)} onChange={() => toggleSelecionado(doc.id)} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-semibold text-primary-400 bg-primary-900/30 px-2 py-0.5 rounded">
                      {doc.tipo?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-slate-300 text-xs">{doc.numero}/{doc.serie}</td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <p className="text-slate-300 text-xs truncate max-w-[160px]">{doc.emitente_razao || '—'}</p>
                    <p className="text-slate-600 text-xs">{doc.emitente_cnpj}</p>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <p className="text-slate-300 text-xs truncate max-w-[160px]">{doc.destinatario_razao || '—'}</p>
                    <p className="text-slate-600 text-xs">{doc.destinatario_cnpj}</p>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-200 text-xs font-medium">
                    {formatarMoeda(doc.valor_total)}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-400 text-xs">
                    {formatarData(doc.data_emissao)}
                  </td>
                  {!operacaoUrl && (
                    <td className="px-4 py-3 hidden md:table-cell">
                      <BadgeOperacao operacao={doc.operacao} />
                    </td>
                  )}
                  <td className="px-4 py-3"><BadgeSituacao situacao={doc.situacao} /></td>
                  {operacaoUrl === 'recebimento' && (
                    <td className="px-4 py-3 hidden md:table-cell">
                      {doc.tipo === 'nfe' && (
                        <BotaoManifesta
                          docId={doc.id}
                          manifestacaoAtual={manifestacoes[doc.id] ?? doc.manifestacao}
                          onAtualizar={atualizarManifestacao}
                        />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-0.5">
                      <button onClick={() => setDocVisualizado(doc.id)} title="Visualizar documento"
                        className="p-1.5 rounded-lg text-slate-500 hover:text-sky-400 hover:bg-sky-900/20 transition-colors">
                        <Eye size={15} />
                      </button>
                      {doc.xml_path && (
                        <button onClick={() => downloadXml(doc.id)} title="Baixar XML"
                          className="p-1.5 rounded-lg text-slate-500 hover:text-primary-400 hover:bg-primary-900/20 transition-colors">
                          <Download size={15} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {paginas > 1 && (
          <div className="px-4 py-3 border-t border-surface-border flex items-center justify-between">
            <p className="text-xs text-slate-600">Página {pagina} de {paginas}</p>
            <div className="flex gap-1">
              <button onClick={() => buscar(pagina - 1)} disabled={pagina === 1}
                className="p-1.5 rounded-lg text-slate-500 hover:text-white disabled:opacity-30 hover:bg-surface-card">
                <ChevronLeft size={16} />
              </button>
              <button onClick={() => buscar(pagina + 1)} disabled={pagina === paginas}
                className="p-1.5 rounded-lg text-slate-500 hover:text-white disabled:opacity-30 hover:bg-surface-card">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {docVisualizado && (
        <DocumentoVisualizadorModal
          docId={docVisualizado}
          onFechar={() => setDocVisualizado(null)}
        />
      )}

      {mostrarFechamento && (
        <ModalFechamento onFechar={() => setMostrarFechamento(false)} />
      )}
    </div>
  );
}
